import { useDispatch } from 'react-redux';
import { Elements, OnLoadParams, Node, Edge } from 'react-flow-renderer';
import {
	NODE_TYPES,
	ReactFlowSchema,
	RehabAction,
	ReactFlowDecisionTree,
} from '@Types';
import { addNodeToNewTree, setNewTreeName, saveRehabTree } from '@Actions';
import { getNodes, getEdges, buildNodeParentsMap } from '@Utils/TreeUtils';
import { cloneMultiParentNodes } from './decisionTreeHelpers';

export interface UseTreeSaveArgs {
	reactFlowInstance: OnLoadParams | null;
	elements: Elements;
	currentTree: ReactFlowDecisionTree;
	rehabActions: RehabAction[];
	rehabDeletedActions: string[];
	shouldEnableMultipleInputs: boolean | string | Object;
	setIsDirty: (dirty: boolean) => void;
}

export const useTreeSave = ({
	reactFlowInstance,
	elements,
	currentTree,
	rehabActions,
	rehabDeletedActions,
	shouldEnableMultipleInputs,
	setIsDirty,
}: UseTreeSaveArgs): { handleSaveTree: () => void } => {
	const dispatch = useDispatch();

	const saveNodes = (nodes: Node[]) => {
		nodes?.forEach(node => {
			const schema: ReactFlowSchema = {
				id: node.id,
				data: {
					label: node.data.label,
					nodeData: {
						...node.data.nodeData,
						title: node.data.label,
					},
				},
				style: node.style,
				type: node.type,
				position: node.position,
				label: node.data.label,
			};
			dispatch(addNodeToNewTree(schema));
		});
	};

	const saveEdges = (edges: Edge[]) => {
		edges?.forEach(edge => {
			const schema: ReactFlowSchema = {
				id: edge.id,
				source: edge.source || '',
				target: edge.target || '',
				label: (edge?.label as string) || '',
				type: 'buttonedge',
			};
			dispatch(addNodeToNewTree(schema));
		});
	};

	const handleSaveTree = () => {
		if (reactFlowInstance) {
			const allElements: Elements = reactFlowInstance.getElements();
			let currentNodes: Node[] = getNodes(allElements);

			// Update current nodes with any changes to actions that have not been picked up by the user changing tabs and the reactFlowInstance updating
			const updatedNodes = getNodes(elements);
			currentNodes = currentNodes.map(node => {
				const updatedNode = updatedNodes.find(
					updated => updated.id === node.id,
				);
				if (updatedNode && updatedNode.type == NODE_TYPES.action) {
					return { ...node, data: updatedNode.data };
				} else {
					return node;
				}
			});

			let currentEdges: Edge[] = getEdges(allElements);
			const nodeParentsMap = buildNodeParentsMap(currentEdges);
			const hasMultiParents = Object.keys(nodeParentsMap).some(
				key => nodeParentsMap[key].length > 1,
			);
			if (shouldEnableMultipleInputs && hasMultiParents) {
				const cloned = cloneMultiParentNodes(
					currentNodes,
					currentEdges,
				);
				saveNodes(cloned.nodes);
				saveEdges(cloned.edges);
			} else {
				saveNodes(currentNodes);
				saveEdges(currentEdges);
			}

			if (currentTree.systemType && currentTree.assetType) {
				dispatch(
					setNewTreeName({
						name: currentTree.name,
						systemType: currentTree.systemType,
						assetType: currentTree.assetType,
						treeNodes: currentTree.nodes,
						actions: rehabActions,
						deletedActions: rehabDeletedActions,
					}),
				);
			}
			dispatch(saveRehabTree(currentTree._id));
			setIsDirty(false);
		}
	};

	return { handleSaveTree };
};
